/**
 * Decouple how often the manager POLLS from how often it FETCHES (ENG-9585).
 *
 * THE PROBLEM THIS SOLVES. `pollCycle` walks every agent on the host
 * SEQUENTIALLY (`for (const agent of processOrder) await processAgent(...)`),
 * and each pass makes four or five HTTP round trips. The configured interval is
 * a GAP applied after the cycle finishes, not a period — so the real per-agent
 * cadence is `interval + cycleDuration`, and the cycle is dominated by network
 * I/O. Measured on prod 2026-08-31: ~84 agents at ~6,860 `/host/refresh`/h is
 * **one pass per agent every ~44s** against a configured interval of 10s. The
 * interval is ~23% of the period; the fetches are the rest.
 *
 * That single knob forces a bad trade. Raising the interval to 30s buys ~31%
 * fewer requests and makes every LOCAL thing slower too — most importantly the
 * scheduler, whose due-check (`getReadyTasks`) runs once per pass, so a task's
 * worst-case lateness is one full period. Lowering it does the reverse.
 *
 * The two do not have to move together. Almost everything a pass does is local:
 * the scheduler due-check reads an in-memory map, the usage-cap gate reads the
 * transcript off disk, the session-health check is a tmux call, the flag reads
 * come from `flags-cache.json`. Only the desired-state fetches need the
 * network, and desired state changes rarely — and when it does change, the
 * Realtime lanes already push it.
 *
 * So: keep the loop fast for the local work, and put a per-agent MINIMUM
 * SPACING on the network calls. The cycle stops being network-bound, the period
 * collapses toward the interval, and the scheduler ticks MORE often than today
 * while the fleet issues far fewer requests.
 *
 * WHY A TTL AND NOT "EVERY NTH CYCLE". A cycle counter measures the wrong
 * thing: cycle duration is a function of how many agents a host runs and how
 * slow the API is that minute, so "every 6th cycle" means something different
 * on a 1-agent host than a 16-agent one, and it drifts when the API slows —
 * exactly when you least want more requests. Wall-clock spacing is the property
 * actually being bought ("ask at most once per N seconds"), so it is the thing
 * to state directly.
 *
 * A FAILED FETCH DOES NOT COUNT AS A FETCH. `markFetched` is called only on
 * success, so a transport error is retried on the very next pass rather than
 * being made to wait out the TTL. This mirrors `kanban-board-fetch.ts`, where
 * retaining a rejection would have imposed one caller's failure policy on
 * another; here it would convert a blip into a guaranteed N-second stall of
 * desired state, which is precisely the case where being current matters most.
 *
 * WHAT IT DELIBERATELY DOES NOT GATE. `/host/my-kanban` is left alone. Its
 * Realtime lane does NOT call `triggerEarlyPoll` — `onTodayItem` injects a
 * kanban-work nudge directly — so unlike the config lanes there is no push to
 * make a slower fetch safe, and the poll's newly-done diff drives user-visible
 * completion notifications. Its duplicate fetch is already being removed
 * separately (#5194), which halves that lane without touching cadence.
 */

/**
 * What a fetch attempt captured when it STARTED. Hand it back to `markFetched`.
 *
 * Exists because of a race that is invisible without it. A fetch is not atomic:
 * a Realtime push can land after the request goes out and before the response
 * comes back. That response was produced BEFORE the change, so consuming the
 * push with it would skip the very refetch the push exists to demand — for a
 * full TTL, with no error and nothing in the log. This is the same
 * swallowed-push failure that `triggerEarlyPoll` guards against at the poll
 * level, one layer down, and it needs the same answer: decide what a fetch
 * consumes from what was true when it STARTED, never from what is true when it
 * lands.
 */
export interface FetchToken {
  /** Per-key force count observed at the start of the attempt. */
  readonly forceCount: number;
  /** Global generation observed at the start of the attempt. */
  readonly generation: number;
}

/** One gated resource, keyed by agent id (or any stable string). */
export interface FetchGate {
  /**
   * True when the caller should hit the network for `key` now: never fetched,
   * a force is outstanding, or the TTL has elapsed since the last SUCCESS.
   */
  shouldFetch(key: string, now?: number): boolean;
  /** Call immediately before the request. Pass the result to `markFetched`. */
  beginFetch(key: string): FetchToken;
  /**
   * Record a SUCCESSFUL fetch. Consumes only the forces that were outstanding
   * when `token` was taken — anything that arrived mid-flight survives.
   */
  markFetched(key: string, token: FetchToken, now?: number): void;
  /** Make the next `shouldFetch` for this key true regardless of the TTL. */
  forceNext(key: string): void;
  /** Force every key currently known, and any key seen later. */
  forceAll(): void;
  /** Drop a key entirely (agent unassigned / torn down). */
  forget(key: string): void;
  /** Test/telemetry: how many keys are being tracked. */
  size(): number;
}

export function createFetchGate(ttlMs: number): FetchGate {
  const lastFetchedAt = new Map<string, number>();

  // COUNTERS, NOT FLAGS. A boolean "is forced" cannot distinguish the force a
  // fetch set out to satisfy from one that arrived while it was in flight, so
  // clearing it would consume both. Counting makes the two comparable: a fetch
  // consumes exactly the count it observed at the start.
  const forceCount = new Map<string, number>();
  const consumedForceCount = new Map<string, number>();

  // Generation covers `forceAll` for keys that do not exist yet: an agent whose
  // first pass has not happened is absent from every map, so stamping known
  // keys would silently skip it.
  let generation = 0;
  const fetchedAtGeneration = new Map<string, number>();

  const pendingForces = (key: string): boolean =>
    (forceCount.get(key) ?? 0) > (consumedForceCount.get(key) ?? 0);

  return {
    shouldFetch(key, now = Date.now()) {
      if (pendingForces(key)) return true;
      if ((fetchedAtGeneration.get(key) ?? -1) < generation) return true;
      const last = lastFetchedAt.get(key);
      if (last === undefined) return true;
      // `>=` so a TTL of 0 means "every time" rather than "every time except
      // when two passes land in the same millisecond".
      return now - last >= ttlMs;
    },
    beginFetch(key) {
      return { forceCount: forceCount.get(key) ?? 0, generation };
    },
    markFetched(key, token, now = Date.now()) {
      lastFetchedAt.set(key, now);
      // Both recorded AS OF THE START of the attempt. A `forceAll` during the
      // flight leaves `generation` ahead of what is stamped here, so the very
      // next `shouldFetch` is true — exactly as if the push had arrived a
      // moment later.
      fetchedAtGeneration.set(key, token.generation);
      consumedForceCount.set(key, token.forceCount);
    },
    forceNext(key) {
      forceCount.set(key, (forceCount.get(key) ?? 0) + 1);
    },
    forceAll() {
      generation += 1;
    },
    forget(key) {
      lastFetchedAt.delete(key);
      fetchedAtGeneration.delete(key);
      forceCount.delete(key);
      consumedForceCount.delete(key);
    },
    size() {
      return lastFetchedAt.size;
    },
  };
}
